import {
  acceptExtNamesFiles,
  maxFilesSize,
} from '@/components/GFileInput/helpers/validations';
import { CompanyFileUploaded } from '@/types/FileType';
import { AxiosPromise, AxiosResponse } from 'axios';
import { acceptExtNames } from '@/helpers/filesFormat';
import { ApiResult, sequence } from '@/core/either';

export interface IFileObject {
  [key: string]: File | File[];
}

export interface IExceededSizeFiles {
  name: string;
  size: number;
}

/**
 * Envía archivos separadamente usando Axios
 * @param fileObj - Objeto con archivos a enviar
 * @param callback - Función que recibe FormData y retorna AxiosPromise
 * @returns Promise con AxiosResponse[]
 */
export async function sendFilesSeparately(
  fileObj: IFileObject,
  callback: (formData: FormData) => AxiosPromise
): Promise<AxiosResponse[]> {
  const promises: AxiosPromise[] = [];

  for (const [keyNameInput, files] of Object.entries(fileObj)) {
    const formData = new FormData();

    if (Array.isArray(files)) {
      files.forEach((file) => {
        formData.append(keyNameInput, file, file.name);
      });
    } else {
      formData.append(keyNameInput, files, files.name);
    }
    if (Array.from((formData as any).entries()).length > 0) {
      const promise = callback(formData);
      promises.push(promise);
    }
  }

  return Promise.all(promises);
}

/**
 * Envía archivos de forma separada usando el patrón Either
 * @param fileObj - Objeto con archivos agrupados por key
 * @param callback - Función que recibe FormData y retorna Promise<ApiResult<any>>
 * @returns Promise con Either conteniendo array de respuestas exitosas o primer error
 */
export async function sendFilesSeparatelyEither<T = any>(
  fileObj: IFileObject,
  callback: (formData: FormData) => Promise<ApiResult<T>>
): Promise<ApiResult<T[]>> {
  const promises: Promise<ApiResult<T>>[] = [];

  for (const [keyNameInput, files] of Object.entries(fileObj)) {
    const formData = new FormData();

    if (Array.isArray(files)) {
      files.forEach((file) => {
        formData.append(keyNameInput, file, file.name);
      });
    } else {
      formData.append(keyNameInput, files, files.name);
    }

    if (Array.from((formData as any).entries()).length > 0) {
      const promise = callback(formData);
      promises.push(promise);
    }
  }

  const results = await Promise.all(promises);

  // Usar la función sequence para convertir array de Either a Either de array
  // Si alguna petición falla, retorna el primer error encontrado
  return sequence(results);
}

export function buildFormDataFiles<T extends IFileObject>(
  fileObj: T
): FormData {
  const formData = new FormData();

  Object.entries(fileObj).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((file) => {
        if (file instanceof File) {
          formData.append(key, file, file.name);
        }
      });
    } else if (value instanceof File) {
      formData.append(key, value, value.name);
    }
  });

  return formData;
}

export function findLinksForName(
  fileNameType: string,
  files: CompanyFileUploaded[]
) {
  if (!files.length) return [];
  const item = files.find((item) => item.fileName === fileNameType);
  return item ? [...item.links, ...item.bucketRoute] : [];
}

export function formatByteStringForBlob(bytesString: string) {
  const byteString = atob(bytesString);
  const byteNumbers = new Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    byteNumbers[i] = byteString.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  return byteArray;
}

export function manageBlobDownload(
  fileBlob: BlobPart,
  documentName: string,
  type: string = 'application/pdf'
) {
  const downloadUrl = URL.createObjectURL(new Blob([fileBlob], { type }));

  const link = document.createElement('a');
  link.setAttribute('href', downloadUrl);
  link.setAttribute('download', documentName);
  link.click();
  URL.revokeObjectURL(downloadUrl);
}

export const maxFileSize = 1024 * 1024 * 10;

export function validateFileStandard(files: File[] | undefined) {
  if (!files?.length) {
    return [
      {
        required: true,
        trigger: 'change',
        validator(rules, value, callback) {
          callback('Debe adjuntar al menos un archivo');
        },
      },
    ];
  }

  return [
    {
      required: true,
      trigger: 'change',
      validator(rules, value, callback) {
        if (files?.length) {
          callback();
        }

        callback('Debe adjuntar al menos un archivo');
      },
    },
    {
      required: true,
      trigger: 'change',
      validator(rules, value, callback) {
        if (!files?.length) callback();

        const { isValid, errorMessage } = acceptExtNamesFiles(
          files,
          acceptExtNames
        );

        if (isValid) {
          callback();
        } else {
          callback(errorMessage || 'Formatos no validos');
        }
      },
    },
    {
      required: true,
      trigger: 'change',
      validator(rules, value, callback) {
        if (!files?.length) callback();

        const { isValid, errorMessage } = maxFilesSize(files, maxFileSize);
        if (isValid) {
          callback();
        } else {
          callback(errorMessage || 'Supera el peso máximo');
        }
      },
    },
  ];
}
